import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { SidebarProvider, useSidebar } from "./SidebarContext";
import { Eye, X } from "lucide-react";
import { endImpersonation, loadSession, maintainSession } from "../../services/api";
import type { ImpersonationSession } from "../../types";

export function AppLayout() {
  return (
    <SidebarProvider>
      <AppShell />
    </SidebarProvider>
  );
}

function AppShell() {
  const navigate = useNavigate();
  const { isMobile, mobileOpen, close } = useSidebar();
  const [imp, setImp] = useState<ImpersonationSession | null>(loadSession()?.impersonation ?? null);

  // re-poll session when route changes so impersonation banner stays in sync
  useEffect(() => {
    setImp(loadSession()?.impersonation ?? null);
  });

  // Keep the operator's session alive while they work and force a clean logout
  // the instant it can no longer be revived — no more frozen, dead-clicking UI.
  // Runs on mount, on a light timer, and whenever the tab regains focus (the
  // classic "came back after lunch" case).
  useEffect(() => {
    const tick = () => { void maintainSession(); };
    tick();
    const timer = window.setInterval(tick, 60_000);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  async function onEndImpersonation() {
    await endImpersonation();
    setImp(null);
    navigate(0);
  }

  return (
    <div className={"app-shell" + (isMobile && mobileOpen ? " sidebar-mobile-open" : "")}>
      <Sidebar />
      {isMobile && mobileOpen ? (
        <div className="sidebar-scrim" onClick={close} aria-hidden />
      ) : null}
      <main className="app-main">
        {imp && (
          <div className="impersonate-banner">
            <div className="row" style={{ gap: 10 }}>
              <Eye size={16} />
              <div>
                Impersonating <strong>{imp.tenantName}</strong> • expires{" "}
                {new Date(imp.expiresAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={onEndImpersonation}>
              <X size={14} /> End session
            </button>
          </div>
        )}
        <Topbar />
        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
